import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { Print, Save, PictureAsPdf } from '@mui/icons-material';
import DOMPurify from 'dompurify';
import membreteService from '../../services/membreteService';
import plantillaService from '../../services/plantillaService';
import './VistaPrevia.css'; // Asegúrate de crear este archivo CSS

const VistaPrevia = ({ 
  contenido, 
  contenido_html, 
  campos, 
  membrete_id,
  datos = {}, 
  paciente = null,
  especialista = null,
  historia = null
}) => {
  const [loading, setLoading] = useState(true);
  const [htmlProcesado, setHtmlProcesado] = useState('');
  const [membrete, setMembrete] = useState(null);
  const [variables, setVariables] = useState({});
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  useEffect(() => {
    const fetchMembrete = async () => {
      try {
        if (membrete_id) {
          const membreteData = await membreteService.getMembreteById(membrete_id);
          setMembrete(membreteData);
        }
      } catch (error) {
        console.error('Error al cargar el membrete:', error);
      }
    };

    const procesarVariables = () => {
      // Variables básicas del sistema
      let vars = {
        fecha_actual: new Date().toLocaleDateString('es-ES'),
        hora_actual: new Date().toLocaleTimeString('es-ES'),
        nombre_clinica: 'NeuroPsico Cusco',
        direccion_clinica: 'Av. Principal 123, Cusco',
        telefono_clinica: '555-1234',
        ...datos
      };

      // Variables de paciente si existe
      if (paciente) {
        vars = {
          ...vars,
          'paciente.nombre': paciente.nombres,
          'paciente.apellidos': paciente.apellidos,
          'paciente.nombre_completo': `${paciente.nombres} ${paciente.apellidos}`,
          'paciente.dni': paciente.dni,
          'paciente.edad': paciente.edad,
          'paciente.telefono': paciente.telefono,
          'paciente.email': paciente.email,
          'paciente.numero_historia': paciente.numero_historia
        };
      }

      // Variables de especialista si existe
      if (especialista) {
        vars = {
          ...vars,
          'especialista.nombre': especialista.usuario?.nombre,
          'especialista.apellidos': especialista.usuario?.apellidos,
          'especialista.nombre_completo': `${especialista.usuario?.nombre} ${especialista.usuario?.apellidos}`,
          'especialista.num_colegiatura': especialista.num_colegiatura
        };
      }

      // Variables de historia clínica si existe
      if (historia) {
        vars = {
          ...vars,
          'historia.fecha_creacion': new Date(historia.fecha_creacion).toLocaleDateString('es-ES'),
          'historia.motivo_consulta': historia.motivo_consulta,
          'historia.diagnostico': historia.diagnostico,
          'historia.plan_tratamiento': historia.plan_tratamiento
        };
      }

      setVariables(vars);
    };

    const procesarHTML = () => {
      let html = contenido_html || '';
      
      // Si hay contenido del editor y no hay HTML, convertir el contenido a HTML
      if (contenido && !html) {
        html = contenido;
      }

      // Reemplazar variables en el HTML
      Object.keys(variables).forEach(key => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        html = html.replace(regex, variables[key] || '');
      });

      // Si hay campos de formulario, generar HTML para ellos
      if (campos && campos.length > 0) {
        let camposHtml = '<div class="formulario-campos">';
        
        campos.forEach(campo => {
          const valor = datos[campo.name] || campo.defaultValue || '';
          
          switch(campo.type) {
            case 'heading':
              camposHtml += `<h3 class="campo-heading">${campo.label}</h3>`;
              break;
            case 'textfield':
            case 'textarea':
            case 'number':
              camposHtml += `
                <div class="campo-container">
                  <label class="campo-label">${campo.label}:</label>
                  <div class="campo-valor">${valor}</div>
                </div>
              `;
              break;
            case 'checkbox':
              camposHtml += `
                <div class="campo-container">
                  <label class="campo-label">${campo.label}:</label>
                  <div class="campo-valor">${valor ? '✓' : '✗'}</div>
                </div>
              `;
              break;
            case 'radio':
            case 'select':
              camposHtml += `
                <div class="campo-container">
                  <label class="campo-label">${campo.label}:</label>
                  <div class="campo-valor">${valor}</div>
                </div>
              `;
              break;
            case 'date':
              const fechaFormateada = valor ? new Date(valor).toLocaleDateString('es-ES') : '';
              camposHtml += `
                <div class="campo-container">
                  <label class="campo-label">${campo.label}:</label>
                  <div class="campo-valor">${fechaFormateada}</div>
                </div>
              `;
              break;
            case 'image':
              camposHtml += `
                <div class="campo-container">
                  <label class="campo-label">${campo.label}</label>
                  <div class="campo-imagen">
                    <img src="${campo.placeholder}" alt="${campo.label}" />
                  </div>
                </div>
              `;
              break;
            default:
              break;
          }
        });
        
        camposHtml += '</div>';
        
        // Agregar campos al HTML (buscar un marcador específico o agregar al final)
        if (html.includes('{{campos_formulario}}')) {
          html = html.replace('{{campos_formulario}}', camposHtml);
        } else {
          html += camposHtml;
        }
      }

      // Sanitizar HTML para evitar XSS
      const sanitizedHtml = DOMPurify.sanitize(html);
      setHtmlProcesado(sanitizedHtml);
      setLoading(false);
    };

    fetchMembrete();
    procesarVariables();
    procesarHTML();
  }, [contenido, contenido_html, campos, membrete_id, datos, paciente, especialista, historia]);

  const generarPDF = async () => {
    setPdfGenerating(true);
    try {
      // Simular la generación del PDF (reemplazar con la llamada real a la API)
      const response = await plantillaService.generarPDF({
        html: htmlProcesado,
        membrete_id: membrete_id,
        datos: datos,
        paciente_id: paciente?.id,
        especialista_id: especialista?.id,
        historia_id: historia?.id
      });
      
      setPdfUrl(response.url);
      setPdfDialogOpen(true);
    } catch (error) {
      console.error('Error al generar PDF:', error);
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={2} display="flex" justifyContent="flex-end">
        <Button
          variant="outlined"
          startIcon={<Print />}
          onClick={() => window.print()}
          sx={{ mr: 1 }}
        >
          Imprimir
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={pdfGenerating ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
          onClick={generarPDF}
          disabled={pdfGenerating}
        >
          Generar PDF
        </Button>
      </Box>
      
      <Paper 
        elevation={3} 
        className="documento-container"
        sx={{ 
          p: 2, 
          position: 'relative',
          minHeight: '842px', // Altura aproximada A4
          width: '100%',
          backgroundColor: 'white'
        }}
      >
        {membrete && (
          <Box 
            className="membrete-container"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '150px',
              backgroundImage: `url(${membrete.imagen})`,
              backgroundSize: 'contain',
              backgroundPosition: 'top center',
              backgroundRepeat: 'no-repeat',
              zIndex: 0
            }}
          />
        )}
        
        <Box 
          className="documento-contenido" 
          sx={{ 
            position: 'relative',
            zIndex: 1,
            pt: membrete ? '150px' : 0
          }}
        >
          <div 
            dangerouslySetInnerHTML={{ __html: htmlProcesado }} 
            className="documento-html"
          />
        </Box>
        
        {membrete && membrete.tipo !== 'Header' && (
          <Box 
            className="membrete-footer-container"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '100px',
              backgroundImage: membrete.tipo === 'Footer' || membrete.tipo === 'Ambos' 
                ? `url(${membrete.imagen})` 
                : 'none',
              backgroundSize: 'contain',
              backgroundPosition: 'bottom center',
              backgroundRepeat: 'no-repeat',
              zIndex: 0
            }}
          />
        )}
      </Paper>
      
      <Dialog
        open={pdfDialogOpen}
        onClose={handleClosePdfDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Documento PDF Generado</DialogTitle>
        <DialogContent>
          {pdfUrl ? (
            <Box height="500px">
              <iframe 
                src={pdfUrl} 
                width="100%" 
                height="100%" 
                style={{ border: 'none' }}
                title="PDF Preview"
              />
            </Box>
          ) : (
            <Typography>No se pudo generar el PDF.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePdfDialog} color="primary">
            Cerrar
          </Button>
          {pdfUrl && (
            <Button 
              component="a" 
              href={pdfUrl} 
              download 
              color="primary" 
              variant="contained"
              startIcon={<Save />}
            >
              Descargar PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VistaPrevia;